import { ArrowRight, FileText } from 'lucide-react';

import { formatMoney, priceFor } from '@repo/contracts/services-pricing';

import { CONTACT_EMAIL, SAMPLE_REPORT_URL } from '../../content/site';
import { ButtonLink, Container, Eyebrow, Tag } from '../ui';

/** A slice of a report, as the hero's picture. Decorative: the real content
 *  is the copy beside it, so it is hidden from assistive tech. */
function ReportPreview() {
  return (
    <div aria-hidden className="relative">
      <div className="rounded-panel border border-hairline bg-surface p-6 shadow-room">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-small-label uppercase text-text-secondary">
              Audit report
            </p>
            <p className="mt-1 text-subtitle font-semibold text-text-primary">
              checkout.example.com
            </p>
          </div>
          <Tag>WCAG 2.2 AA</Tag>
        </div>
        <div className="mt-5 grid grid-cols-4 gap-2">
          {[
            ['Critical', '4', 'bg-tag-critical'],
            ['Serious', '11', 'bg-tag-major'],
            ['Moderate', '17', 'bg-tag-minor'],
            ['Minor', '9', 'bg-warm'],
          ].map(([label, n, fill]) => (
            <div className={`rounded-thumb p-3 ${fill}`} key={label}>
              <p className="font-report text-display-title font-medium text-text-primary">
                {n}
              </p>
              <p className="text-caption text-text-secondary">{label}</p>
            </div>
          ))}
        </div>
        <div className="mt-5 space-y-3">
          <div className="rounded-thumb border border-hairline border-l-4 border-l-critical p-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-small-label uppercase text-text-primary">
                Critical
              </span>
              <Tag>2.1.2 No keyboard trap</Tag>
              <Tag tone="success">Verified by specialist</Tag>
            </div>
            <p className="mt-2 text-ui-strong font-semibold text-text-primary">
              Focus is trapped inside the address autocomplete
            </p>
          </div>
          <div className="rounded-thumb border border-hairline border-l-4 border-l-warning p-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-small-label uppercase text-text-primary">
                Serious
              </span>
              <Tag>1.4.11 Non-text contrast</Tag>
            </div>
            <p className="mt-2 text-ui-strong font-semibold text-text-primary">
              Card number field border is 1.6:1 against the page
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function Hero() {
  return (
    <section
      aria-labelledby="hero-title"
      className="pt-14 pb-20 md:pt-24 md:pb-28"
    >
      <Container>
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Eyebrow>
              Expert accessibility audits · WCAG 2.2 · EN 301 549
            </Eyebrow>
            <h1
              className="mt-4 text-balance font-report text-report-title font-medium text-text-primary"
              id="hero-title"
            >
              Know exactly where your site fails accessibility&nbsp;— and how to
              fix it.
            </h1>
            <p className="mt-6 max-w-lg text-report-lede text-text-secondary">
              Specialist-verified accessibility audits in days, not weeks — at a
              fraction of the cost of a manual audit.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <ButtonLink to="/request">
                Request an audit <ArrowRight aria-hidden size={18} />
              </ButtonLink>
              <ButtonLink
                to={
                  SAMPLE_REPORT_URL ??
                  `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('Sample accessibility report')}`
                }
                variant="secondary"
              >
                <FileText aria-hidden size={18} /> See a sample report
              </ButtonLink>
            </div>
            <p className="mt-4 text-ui text-text-secondary">
              {formatMoney(priceFor(1).ratePerPage)} per page. Free to request —
              pay only after scoping.
            </p>
          </div>
          <div className="lg:col-span-6">
            <ReportPreview />
          </div>
        </div>
      </Container>
    </section>
  );
}
